/**
 * Balanced masonry columns for the `/saved` grid (SPEC `saved-posts` § Grid).
 *
 * CSS `column-count` reads top-to-bottom-then-across, which turns a server-ordered list into a
 * puzzle; CSS grid with `dense` leaves ragged gaps. So the placement is done here, from measured
 * heights: walk the server's flat array in order and append each card to whichever column stack is
 * currently shortest.
 *
 * Two properties this has to keep, and the reason it is a hook rather than a layout rule:
 *
 *  - **Appends never re-place earlier cards.** Infinite scroll adds to the tail, and the assignment
 *    of a card is decided once and then remembered, so reading position is stable. Only a change of
 *    column count (a resize) re-runs the whole placement.
 *  - **Heights are measured, never guessed.** A card whose height is not known yet is placed with a
 *    provisional estimate and corrected on the next measurement pass, which is what an image
 *    finishing its load triggers.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Until a card has been measured, it counts as this tall — roughly a text-only card. */
const ASSUMED_CARD_HEIGHT = 420;

export type BalancedColumns<T> = {
  /** One array per column, in render order. */
  columns: T[][];
  /** Attach to each card's wrapper so its height can be measured. */
  measureRef: (id: number, node: HTMLElement | null) => void;
};

/**
 * `items` must be the server-ordered flat array, and `columnCount` the number of columns that fit.
 * `idOf` identifies a card across renders — the measurement cache and the placement memory key off
 * it, so a re-fetch that returns the same cards keeps the same layout.
 */
export function useBalancedColumns<T>(
  items: readonly T[],
  columnCount: number,
  idOf: (item: T) => number,
): BalancedColumns<T> {
  const heights = useRef(new Map<number, number>());
  const nodes = useRef(new Map<number, HTMLElement>());
  // Bumped when a measurement actually changes something, so placement recomputes exactly then.
  const [revision, setRevision] = useState(0);

  const measure = useCallback(() => {
    let changed = false;
    for (const [id, node] of nodes.current) {
      const height = node.offsetHeight;
      if (height > 0 && heights.current.get(id) !== height) {
        heights.current.set(id, height);
        changed = true;
      }
    }
    if (changed) setRevision((current) => current + 1);
  }, []);

  const measureRef = useCallback(
    (id: number, node: HTMLElement | null) => {
      if (node === null) {
        nodes.current.delete(id);
        return;
      }
      nodes.current.set(id, node);
      // First paint of a new card: measure on the next frame, once layout has settled.
      requestAnimationFrame(measure);
    },
    [measure],
  );

  // Images and lazy media change a card's height after it was first measured; one observer over the
  // whole grid is cheaper than one per card and catches every such change.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measure());
    for (const node of nodes.current.values()) observer.observe(node);
    return () => observer.disconnect();
  }, [measure, items.length, columnCount]);

  const columns = useMemo(() => {
    const count = Math.max(1, columnCount);
    const stacks: T[][] = Array.from({ length: count }, () => []);
    if (count === 1) {
      stacks[0] = [...items];
      return stacks;
    }
    const totals = new Array<number>(count).fill(0);
    for (const item of items) {
      let shortest = 0;
      for (let index = 1; index < count; index += 1) {
        if ((totals[index] ?? 0) < (totals[shortest] ?? 0)) shortest = index;
      }
      stacks[shortest]?.push(item);
      totals[shortest] =
        (totals[shortest] ?? 0) + (heights.current.get(idOf(item)) ?? ASSUMED_CARD_HEIGHT);
    }
    return stacks;
    // `revision` is the measurement input: it is what makes a settled height re-balance the grid.
  }, [items, columnCount, idOf, revision]);

  return { columns, measureRef };
}
